// @flow

import Downshift from 'downshift';
// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node, useRef } from 'react';
import { omit } from 'lodash';

import Icon from '@zapier/design-system/src/components/display/Icon';
import FloatingMenu, {
  type FloatingMenuGetLabelForItem,
  type FloatingMenuItem,
  type FloatingMenuProps,
} from '@zapier/design-system/src/components/forms/FloatingMenu';
import Input, {
  type InputEvent,
  type InputProps,
} from '@zapier/design-system/src/components/forms/Input';
import { Colors } from '@zapier/design-system/src/theme';
import { camelCaseAriaAttrs } from '@zapier/design-system/src/utils/formatAriaAttrKeys';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

export type FloatingMenuRenderProps = {
  /**
   * Start with the default FloatingMenu's props
   */
  ...$Exact<FloatingMenuProps>,

  /**
   * Action to manually select an item
   */
  selectItem: (item: FloatingMenuItem) => void,
  /**
   * The currently selected item (if any)
   */
  selectedItem: FloatingMenuItem,
  /**
   * Set the currently highlighted item index
   */
  setHighlightedIndex: (index: number) => void,
  /**
   * Clear the selection
   */
  clearSelection: () => void,
  /**
   * Close the menu
   */
  closeMenu: () => void,
};

export type OnBlurMetadata = {
  /**
   * Did the blur occur within the dropdown control?
   * i.e. is some child of the Dropdown still focused?
   */
  didBlurWithin: boolean,
  /**
   * Close the menu
   */
  closeMenu: () => void,
};

export type Props = {|
  ...$Exact<InputProps>,
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.  Defaults to the index of each item in the array.
   */
  getKeyForItem?: (item: FloatingMenuItem) => string,
  /**
   * Callback that will be used to generate the string label to be rendered for
   * each item on the menu list.
   */
  getLabelForItem: FloatingMenuGetLabelForItem,
  /**
   * HTML `id` attribute for the `button` element. This prop is named `inputId`
   * instead of `id` for consistency with the Downshift API.
   */
  inputId?: string,
  /**
   * The list of items.
   */
  items: Array<FloatingMenuItem>,
  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  menuAriaLabel: string,
  /**
   * Callback executed when the input is blurred.
   */
  onBlur?: (event: InputEvent, meta: OnBlurMetadata) => void,
  /**
   * Callback that gets called when the user selects an item and the selected item has changed.
   */
  onChange: (item: FloatingMenuItem) => void,
  /**
   * Callback executed when the input is focused.
   */
  onFocus?: (event: InputEvent) => void,
  /**
   * Optional prop for setting the initially selected item, but still letting `Downshift` manage
   * the selected item state for you.
   */
  initialSelectedItem?: FloatingMenuItem,
  /**
   * A render prop which receives a menu `item` and returns a React element,
   * which is an icon illustrating the given menu item. The natural size for
   * the icon is 30x30px. If not provided, no icons will be rendered.
   */
  renderIcon?: (item: FloatingMenuItem) => Node,
  /**
   * A render prop for creating the floating menu.
   */
  renderFloatingMenu: FloatingMenuRenderProps => Node,
  /**
   * A render prop for the button content given the selected item
   */
  renderButtonForItem?: (item: FloatingMenuItem) => Node,
|};

const isDisabled = (props: Props) => {
  return props.isDisabled || props.items.length === 0;
};

const Styles = labelStyles(__filename, {
  root: {
    position: 'relative',
  },

  buttonWrap: (props: Props) => ({
    cursor: isDisabled(props) ? 'default' : 'pointer',
  }),

  buttonText: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  placeholder: (
    props: Props,
    derivedStated: { isHovered: boolean, isFocused: boolean }
  ) => [
    // Note that the order of these array items is important for specificity.
    {
      color: Colors.grey700,
    },
    !isDisabled(props) &&
      (derivedStated.isHovered || derivedStated.isFocused) &&
      withModifier('hovered-or-focused', {
        color: Colors.blue500,
      }),
    props.isErrored &&
      withModifier('errored', {
        color: Colors.red500,
      }),
    isDisabled(props) &&
      withModifier('disabled', {
        color: Colors.grey700,
      }),
  ],
});

const buildAriaLabel = ({
  label,
  value,
  placeholder,
}: {
  label: string,
  value?: string,
  placeholder?: string,
}) => {
  return [value, label, !value && placeholder].filter(Boolean).join(', ');
};

/**
 * Basic dropdown component that should be used when a smallish
 * number of items need to be chosen from, roughly fewer than 20.
 * If there are substantially more items, consider `Typeahead`.
 *
 * Tags: core-ui, forms
 */
const Dropdown = (props: Props) => {
  const parentRef = useRef();
  const itemToString = (item?: FloatingMenuItem) => {
    return item ? props.getLabelForItem(item) : '';
  };

  const onChangeNoNull = selection => {
    if (selection) {
      props.onChange(selection);
    }
  };

  const stateReducer = (state, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEscape:
        // When you open the dropdown and press `esc`, it will clear the selection by default
        // We don't want this functionality, so overriding it here.
        // See: https://github.com/downshift-js/downshift/issues/680
        return {
          isOpen: false,
        };
      default:
        return changes;
    }
  };

  const renderButtonForItem =
    props.renderButtonForItem || props.getLabelForItem;

  return (
    <Downshift
      getLabelForItem={props.getLabelForItem}
      initialSelectedItem={props.initialSelectedItem}
      inputId={props.inputId}
      itemCount={props.items.length}
      itemToString={itemToString}
      onChange={onChangeNoNull}
      stateReducer={stateReducer}
    >
      {({
        clearSelection,
        closeMenu,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        highlightedIndex,
        isOpen,
        openMenu,
        selectItem,
        selectedItem,
        setHighlightedIndex,
      }) => {
        const hasSelectedItem = !!selectedItem;
        const renderIcon = () =>
          typeof props.renderIcon === 'function'
            ? props.renderIcon(selectedItem)
            : undefined;
        // Remove some DOM attrs that axe complains about.
        // These should be redundant given `ariaLabel` and `inputId`.
        const rootProps = omit(getRootProps(), ['aria-labelledby', 'role']);

        const onBlur = e => {
          if (props.onBlur) {
            const meta = {
              didBlurWithin:
                parentRef.current &&
                parentRef.current.contains(e.relatedTarget),
              closeMenu,
            };
            props.onBlur(e, meta);
          }
          // If there is no custom onBlur behavior, then just close the menu
          else {
            closeMenu();
          }
        };

        return (
          <div css={Styles.root} {...rootProps} onBlur={onBlur} ref={parentRef}>
            <div css={Styles.buttonWrap(props)}>
              <Input
                onClick={() => {
                  if (isOpen === true) {
                    closeMenu();
                  }
                }}
                {...props}
                {...camelCaseAriaAttrs(getInputProps())}
                canClickIconAfter={false}
                id={props.inputId}
                isDisabled={isDisabled(props)}
                renderIconAfter={() => (
                  <Icon name="upAndDownArrows" size={24} />
                )}
                renderIconBefore={renderIcon}
              >
                {(inputProps, derivedState) => (
                  // Intentionally `button` and not `Button` since it looks
                  // and behaves differently.
                  <button
                    {...{
                      // `Downshift` requires this attribute to work properly, so
                      // there's an a11y balance to perform here. This is spread as
                      // an object since the `eslint-disable-next-line` comment didn't
                      // work for JSX props.
                      // eslint-disable-next-line jsx-a11y/role-supports-aria-props
                      'aria-activedescendant':
                        inputProps['aria-activedescendant'],
                    }}
                    aria-controls={inputProps['aria-controls']}
                    aria-expanded={isOpen ? 'true' : 'false'}
                    aria-haspopup="true"
                    aria-invalid={inputProps['aria-invalid']}
                    aria-label={buildAriaLabel({
                      label: inputProps['aria-label'],
                      placeholder: props.placeholder,
                      value: hasSelectedItem
                        ? props.getLabelForItem(selectedItem)
                        : undefined,
                    })}
                    css={[inputProps.css]}
                    disabled={inputProps.disabled}
                    id={inputProps.id}
                    onBlur={onBlur}
                    onChange={onChangeNoNull}
                    onClick={isDisabled(props) ? undefined : openMenu}
                    onFocus={props.onFocus}
                    onKeyDown={inputProps.onKeyDown}
                    type="button"
                  >
                    {hasSelectedItem ? (
                      <span css={Styles.buttonText}>
                        {renderButtonForItem(selectedItem)}
                      </span>
                    ) : (
                      <span
                        css={[
                          Styles.buttonText,
                          Styles.placeholder(props, derivedState),
                        ]}
                      >
                        {props.placeholder}
                      </span>
                    )}
                  </button>
                )}
              </Input>
            </div>
            {isOpen &&
              props.renderFloatingMenu({
                ariaLabel: props.menuAriaLabel,
                clearSelection,
                closeMenu,
                items: props.items,
                highlightedIndex,
                getKeyForItem: props.getKeyForItem,
                getLabelForItem: props.getLabelForItem,
                getItemProps,
                getMenuProps,
                getInputProps,
                renderIcon: props.renderIcon,
                selectItem,
                selectedItem,
                setHighlightedIndex,
              })}
          </div>
        );
      }}
    </Downshift>
  );
};

Dropdown.defaultProps = {
  color: 'grey100',
  size: 'small',
  getLabelForItem: item => item.label,
  renderFloatingMenu: props => <FloatingMenu {...props} />,
};

export default Dropdown;
